// ============================================================
// DEMO CUSTOMER — always-available "view as customer" target
// ============================================================
// Netcom-admins need a reliable way to:
//   1. Show prospects + new hires what the customer view looks like
//   2. Test product changes without poking real customer data
//   3. Get a working demo even when the dev API is mid-deploy
//
// The demo customer is a synthetic account built from the existing
// mock fixtures (50-dish CTO persona). Switching into it doesn't
// touch the backend — the SPA fetch interceptor sees the demo flag
// and serves data from src/data/mock/* instead of /admin/assume.
//
// IMPORTANT: starting a demo session does NOT write an audit row.
// Real impersonation does (via POST /admin/assume → S3 Object Lock).
// The demo session is a UI-only sandbox.

import { startImpersonation, exitImpersonation } from './impersonation.js';
import { getVertical } from '../data/mock/verticals.js';

const DEMO_KEY = 'netcom_connect_demo_customer_session';
// Flagship landing fleet for the public no-login demo (Oil & Gas, large).
// Visitors can switch to Logistics / Retail in-app via the account switcher.
const FLAGSHIP_DEMO_ACCT = 'ACC-DEMO-CTO-50FLEET-01';

/**
 * The pinned demo customer. Mirrors the 50-dish CTO persona used
 * throughout the mock fixtures. Account number aligns with the
 * mock data alias (ACC-CTO-50-FLEET) so every page renders.
 */
export const DEMO_CUSTOMER = {
  username: 'demo-customer-globaltrade',
  email: 'cto@globaltrade.example',
  firstName: 'Morgan',
  lastName: 'Patel',
  businessName: 'Globaltrade Energy (Demo)',
  accountNumber: 'ACC-CTO-50-FLEET',
  koreAcctId: 'cmp-k1-org-demo-globaltrade',
  products: ['starlink', 'sim'],
  status: 'CONFIRMED',
  enabled: true,
  isStaff: false,
  isDemo: true,
  // Surface text used by the AdminAccounts pinned card
  summary: '64 wellsites & rigs · Permian / Bakken / Gulf · mission-critical SLA',
};

/**
 * Start a demo-impersonation session. Records a sessionStorage flag
 * the SPA reads from on every page load. Routing the admin to "/"
 * then renders all dashboards against mock data.
 *
 * Unlike real impersonation:
 *   - No /admin/assume call
 *   - No audit row
 *   - No JWT swap
 *   - Cleanly dismissable via endDemo() — no expiry-cleanup needed
 */
export function startDemo() {
  const session = {
    startedAt: Date.now(),
    actorEmail: 'demo-mode-local',
    customer: DEMO_CUSTOMER,
  };
  try {
    sessionStorage.setItem(DEMO_KEY, JSON.stringify(session));
  } catch { /* private-mode storage cap — ignore */ }
  return session;
}

/** Read the current demo session if active. Returns null when not in demo mode. */
export function getDemo() {
  try {
    const raw = sessionStorage.getItem(DEMO_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

/** Returns true when the current session is a demo session. */
export function isDemoMode() {
  return !!getDemo();
}

/** End the demo session. Reloads the page so cached fetches re-run.
 *  Guests return to /login; an admin who opened the demo card returns to
 *  the admin users page. Always clears impersonation so the underlying
 *  identity (if any) is restored. */
export function endDemo({ reload = true } = {}) {
  const d = getDemo();
  const wasGuest = !!(d && d.guest);
  const wasConsole = !!(d && d.fromConsole);
  try { sessionStorage.removeItem(DEMO_KEY); } catch { /* ignore */ }
  try { exitImpersonation(); } catch { /* ignore */ }
  if (reload && typeof window !== 'undefined') {
    // Guests → login; a Program Console demo → back to the console ('/');
    // an admin who opened the demo from the admin pages → back there.
    window.location.assign(wasGuest ? '/login' : wasConsole ? '/' : '/admin/users');
  }
}

/**
 * Enter the demo from the Program Console (internal staff). Same mock-data
 * sandbox as the admin "Open Demo Customer", but tagged `fromConsole` so
 * ending the demo returns to the console instead of the admin users page.
 * Sets the demo session + impersonates the flagship fleet so every page
 * resolves. Caller reloads to '/'.
 */
export function enterConsoleDemo(accountNumber = FLAGSHIP_DEMO_ACCT) {
  const v = getVertical(accountNumber) || getVertical(FLAGSHIP_DEMO_ACCT);
  const acc = v?.account?.accountNumber || FLAGSHIP_DEMO_ACCT;
  const session = {
    startedAt: Date.now(),
    actorEmail: 'console-demo',
    fromConsole: true,
    customer: {
      ...DEMO_CUSTOMER,
      accountNumber: acc,
      businessName: v?.account?.accountName || DEMO_CUSTOMER.businessName,
    },
  };
  try { sessionStorage.setItem(DEMO_KEY, JSON.stringify(session)); } catch { /* private-mode safe */ }
  try {
    startImpersonation({
      accountNumber: acc,
      businessName: v?.account?.accountName || acc,
      email: v?.account?.authorizedRepresentative?.email || null,
      actorEmail: 'console-demo',
    });
  } catch { /* impersonation best-effort; mock data still resolves */ }
  return session;
}

// ── PUBLIC GUEST DEMO (no login) ────────────────────────────
// A prospect clicks "View a live demo" on the login page. We start a demo
// session (so every read serves mock data) + impersonate a vertical (so
// getAccountNumber resolves its fleet) and tag it `guest` so auth.js treats
// the visitor as authenticated for the duration. SAFE because
// installDemoFetchGuard() hard-blocks every real-backend request while a
// demo session is active — the guest can never reach Cognito, the API
// gateway, KORE, or Starlink.

/** Start a public, no-login demo on the given vertical (default: flagship). */
export function startGuestDemo(accountNumber = FLAGSHIP_DEMO_ACCT) {
  const v = getVertical(accountNumber) || getVertical(FLAGSHIP_DEMO_ACCT);
  const acct = v?.account?.accountNumber || FLAGSHIP_DEMO_ACCT;
  const session = {
    startedAt: Date.now(),
    actorEmail: 'guest-demo',
    guest: true,
    customer: {
      ...DEMO_CUSTOMER,
      accountNumber: acct,
      businessName: v?.account?.accountName || DEMO_CUSTOMER.businessName,
    },
  };
  try { sessionStorage.setItem(DEMO_KEY, JSON.stringify(session)); } catch { /* private-mode safe */ }
  try {
    startImpersonation({
      accountNumber: acct,
      businessName: v?.account?.accountName || acct,
      email: v?.account?.authorizedRepresentative?.email || null,
      actorEmail: 'guest-demo',
    });
  } catch { /* impersonation is best-effort; mock data still resolves */ }
  return session;
}

/** True when the active demo session is a public guest (not an admin). */
export function isGuestDemo() {
  const d = getDemo();
  return !!(d && d.guest);
}

/** Synthetic session for a guest visitor so AuthGate + pages render.
 *  Carries no real token, no admin groups — purely a mock identity. */
export function getDemoSession() {
  const d = getDemo();
  if (!d) return null;
  const c = d.customer || DEMO_CUSTOMER;
  return {
    id: d.guest ? 'demo-guest' : 'demo-admin',
    sub: d.guest ? 'demo-guest' : 'demo-admin',
    email: c.email,
    name: [c.firstName, c.lastName].filter(Boolean).join(' '),
    accountNumber: c.accountNumber,
    products: c.products || ['starlink', 'sim'],
    // Owner role so the demo showcases the full self-service management surface
    // (suspend/restore, IMEI lock, remote reboot, SMS). Demo-only — real
    // customers get their actual Cognito role.
    role: c.role || 'owner',
    groups: [],
    isDemo: true,
    guest: !!d.guest,
  };
}

/** Install a fetch kill-switch: while ANY demo session is active, every
 *  request to a real backend (API gateway / Cognito / AWS) is short-circuited
 *  with a benign empty response. This is the safety net that makes a public,
 *  no-login demo safe on a build that still carries real auth + API config —
 *  even a code path that forgot to check mock-mode physically cannot reach a
 *  real system. Call once at app boot, AFTER the other fetch interceptors so
 *  this one evaluates first. */
export function installDemoFetchGuard() {
  if (typeof window === 'undefined' || window.__netcomDemoFetchInstalled) return;
  window.__netcomDemoFetchInstalled = true;
  const apiBase = (import.meta.env?.VITE_API_BASE || '/api').replace(/\/$/, '');
  const origFetch = window.fetch.bind(window);
  window.fetch = (input, init = {}) => {
    if (!isDemoMode()) return origFetch(input, init);
    const url = typeof input === 'string' ? input : (input && input.url) || '';
    const isBackend =
      (apiBase && apiBase !== '/api' && url.startsWith(apiBase)) ||
      /\/api\//.test(url) ||
      /execute-api|amazonaws\.com|cognito-idp|amazoncognito\.com/i.test(url);
    if (isBackend) {
      // Never reach a real backend in demo. Return a benign stub so callers
      // that don't check mock-mode degrade gracefully instead of throwing.
      return Promise.resolve(
        new Response(JSON.stringify({ demo: true, blocked: true, items: [], results: [] }), {
          status: 200,
          headers: { 'Content-Type': 'application/json' },
        }),
      );
    }
    return origFetch(input, init);
  };
}
